import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { RootState, AppDispatch } from '../../app/store'
import { loginPin } from '../../features/auth/authSlice'
import toast from 'react-hot-toast'
import {
  LiftPage,
  NumericKeyboard,
  FlutterButton,
  PinBox,
  BackButton
} from '../../components/auth'

const LoginPinPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  
  const { isLoading } = useSelector((state: RootState) => state.auth)
  const { settings } = useSelector((state: RootState) => state.settings)
  
  const [pin, setPin] = useState('')
  const [employeeId, setEmployeeId] = useState<string | null>(null)

  const isRTL = settings.language === 'ar'

  // Prevent scrolling and manage body class
  useEffect(() => {
    document.body.classList.add('no-scroll')
    
    // Get employeeId from localStorage
    const storedEmployeeId = localStorage.getItem('temp_employee_id')
    if (storedEmployeeId) {
      setEmployeeId(storedEmployeeId)
    } else {
      // If no employeeId, redirect back to login
      navigate('/login', { replace: true })
    }
    
    return () => {
      document.body.classList.remove('no-scroll')
    }
  }, [navigate])

  const handleSubmit = async () => {
    if (!employeeId || pin.length !== 5) {
      toast.error(t('validation.required_field'))
      return
    }
    
    const resultAction = await dispatch(loginPin({ username: employeeId, password: pin }) as any)
    
    if (loginPin.fulfilled.match(resultAction)) {
      localStorage.removeItem('temp_employee_id') // Clear temp ID after successful login
      
      // Navigate based on user type and shift status - matches Flutter logic
      // Handle both payload structures: { data: {...} } or direct data
      const payload = resultAction.payload
      const data = payload.data || payload
      const user = data?.employee || payload?.employee || data?.user || payload?.user
      const hasActiveShift = user?.has_active_shift || false
      
      // Wait a brief moment to ensure Redux state is fully updated
      // Then navigate based on user type and shift status
      setTimeout(() => {
        if (user?.type === 'customer_service') {
          navigate('/home', { replace: true })
        } else if (hasActiveShift) {
          navigate('/home', { replace: true })
        } else {
          navigate('/shift-opening', { replace: true })
        }
      }, 100) // Small delay to ensure state update
    } else if (loginPin.rejected.match(resultAction)) {
      toast.error(resultAction.payload as string || t('auth.login_failed'))
    } else {
      toast.error(t('auth.login_failed'))
    }
  }

  const addNumber = (number: string) => {
    if (pin.length < 5) {
      setPin(prev => prev + number)
    }
  }

  const deleteNumber = () => {
    setPin(prev => prev.slice(0, -1))
  }

  return (
    <div className="h-screen bg-[#F5F5F5] flex overflow-hidden">
      {/* Back button */}
      <div className='m-15 mt-24'>

      <BackButton isRTL={isRTL} />

      </div>
      
      {/* Main content area */}
      <div className="flex-1 flex flex-col items-center justify-center" style={{ direction: 'ltr' }}>
        {/* Title */}
        <h1 
          className="text-black font-medium mb-[clamp(12px,1.5vw,16px)] text-[clamp(20px,2.2vw,32px)] font-['IBM Plex Sans Arabic']"
          style={{ textAlign: isRTL ? 'right' : 'left' }}
        >
          {t('auth.enter_pin')}
        </h1>
        
        {/* Employee ID Display - like Flutter */}
        <div className="mb-[clamp(12px,1.5vw,16px)] px-[clamp(12px,1.5vw,20px)] py-[clamp(6px,0.75vw,8px)]">
          <p className="text-[#757575] text-[clamp(12px,1.2vw,18px)] font-['IBM Plex Sans Arabic']">
            {isRTL 
              ? `كود الموظف: ${employeeId}` 
              : `Employee ID: ${employeeId}`
            }
          </p>
        </div>
        
        {/* Container for consistent width */}
        <div className="w-full max-w-[clamp(320px,65vw,580px)]  flex flex-col items-center px-[clamp(12px,1.5vw,16px)]">
          {/* PIN boxes */}
          <div className="flex justify-center mb-[clamp(8px,1vw,12px)] w-full">
            {[0, 1, 2, 3, 4].map(index => (
              <PinBox key={index} value={pin[index] || ''} />
            ))}
          </div>
          
          {/* Spacing */}
          <div className="h-[clamp(4px,0.5vw,8px)]"></div>
          
          {/* Keyboard */}
          <div className="w-full mb-[clamp(12px,1.5vw,16px)]">
            <NumericKeyboard 
              onNumberPress={addNumber}
              onDeletePress={deleteNumber}
              disabled={isLoading}
            />
          </div>
          
          {/* Spacing */}
          <div className="h-[clamp(8px,1vw,16px)]"></div>
          
          {/* Button - aligned with keyboard width */}
          <div className="w-full">
            <FlutterButton
              text={t('common.done')}
              onPress={handleSubmit}
              disabled={pin.length !== 5}
              isLoading={isLoading}
              loadingText={t('common.loading')}
            />
          </div>
        </div>
      </div>   
      {/* Lift page */}
      <LiftPage />
    </div>
  )
}

export default LoginPinPage